import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge, Card, EmptyState, ErrorState, Loading, PageHeader } from '@/components/ui';

/**
 * Soundboard Bots, as a client sees them.
 *
 * Which is: that they exist, what they are for, and nothing else. The
 * recordings belong to the operator and there is nothing here to configure —
 * a settings page with nothing usable on it is a support call waiting to
 * happen, so this page does not pretend to be one.
 *
 * A client whose account does not include the product sees a short
 * explanation rather than an error. They have not been refused anything.
 */

interface Board {
  id: string;
  name: string;
  code: string;
  status: string;
  service: string | null;
  clips: number;
}

export default function SoundboardBots() {
  const boards = useQuery({
    queryKey: ['soundboards'],
    queryFn: () => api.get<{ data: Board[]; enabled: boolean }>('/soundboards'),
  });

  if (boards.isPending) return <Loading label="Loading…" />;
  if (boards.error) return <ErrorState error={boards.error} retry={() => boards.refetch()} />;

  if (!boards.data?.enabled) {
    return (
      <div>
        <PageHeader
          title="Soundboard Bots"
          description="Recorded screening calls, transferred to your team only when the customer qualifies."
        />
        <Card title="Not on this account">
          <p className="text-sm text-ink-600 dark:text-ink-300">
            A Soundboard Bot plays recordings you supply, listens to the answer,
            and transfers only the people who confirm they own their home. It is
            not part of your plan at the moment — ask your account manager to
            add it.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Soundboard Bots"
        description="Recorded screening calls. Only customers who confirm they own their home are transferred."
      />

      {!boards.data.data.length ? (
        <EmptyState
          title="No soundboards yet"
          description="Your account manager sets these up with the recordings you provide."
        />
      ) : (
        <Card title="Your boards">
          <div className="divide-y divide-line">
            {boards.data.data.map((board) => (
              <div key={board.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">
                    {board.name}{' '}
                    <span className="text-xs text-ink-500">{board.code}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-500">
                    {board.service && <span>{board.service}</span>}
                    <span>
                      {board.clips} recording{board.clips === 1 ? '' : 's'}
                    </span>
                    <Badge tone={board.status === 'ACTIVE' ? 'green' : 'gray'}>
                      {board.status.toLowerCase()}
                    </Badge>
                  </div>
                </div>
                <Link
                  to="/calls"
                  className="text-xs text-brand-600 underline dark:text-brand-300"
                >
                  Calls
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-ink-500">
            The recordings and the screening questions are managed for you. Every
            call these make appears on your Calls page with its transcript and
            outcome, like any other.
          </p>
        </Card>
      )}
    </div>
  );
}
